// System Status Service - SOLO LECTURA
// Lee system/config documento de Firestore en tiempo real
// La ruleta OBEDECE el estado, NO lo modifica
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

// Path del documento de configuración
const CONFIG_DOC_PATH = 'system/config';

// Versión de la app (para comparar con minVersion)
// TODO: Mover a variable de entorno o config cuando se implemente versionado
const APP_VERSION = null; // null = no verificar versión aún

/**
 * Suscribirse al estado del sistema en tiempo real
 * Lee: status, maintenance, message, minVersion
 * 
 * @param {Function} callback - Función que recibe el estado completo
 * @returns {Function} - Función para cancelar la suscripción
 */
export const subscribeToSystemStatus = (callback) => {
  const configRef = doc(db, CONFIG_DOC_PATH);
  
  const unsubscribe = onSnapshot(
    configRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        
        // Leer todos los campos
        const status = data.status || 'inactive';
        const maintenance = data.maintenance === true;
        const message = data.message || '';
        const minVersion = data.minVersion || 0;
        
        // Determinar si el sistema está activo
        // Bloqueado si: status !== 'active' OR maintenance === true OR (minVersion > APP_VERSION cuando APP_VERSION existe)
        const isStatusActive = status === 'active';
        const isInMaintenance = maintenance === true;
        const isVersionBlocked = APP_VERSION !== null && minVersion > APP_VERSION;
        
        // El sistema está activo solo si pasa TODAS las verificaciones
        const isActive = isStatusActive && !isInMaintenance && !isVersionBlocked;
        
        // Determinar el mensaje de bloqueo prioritario
        let blockReason = null;
        if (isInMaintenance) {
          blockReason = 'maintenance';
        } else if (!isStatusActive) {
          blockReason = 'inactive';
        } else if (isVersionBlocked) {
          blockReason = 'version';
        }
        
        callback({
          // Estado general
          isActive,
          blockReason,
          
          // Campos individuales
          status,
          maintenance,
          message, // Mensaje informativo (puede mostrarse sin bloquear)
          minVersion,
          
          // Flags específicos
          isStatusActive,
          isInMaintenance,
          isVersionBlocked,
          
          // Versión de la app
          appVersion: APP_VERSION,
          
          // Mensajes predefinidos según razón de bloqueo
          blockMessage: getBlockMessage(blockReason)
        });
      } else {
        // Documento no existe - sistema inactivo por seguridad
        callback({
          isActive: false,
          blockReason: 'no_config',
          status: 'inactive',
          maintenance: false,
          message: '',
          minVersion: 0,
          isStatusActive: false,
          isInMaintenance: false,
          isVersionBlocked: false,
          appVersion: APP_VERSION,
          blockMessage: 'Configuración del sistema no encontrada'
        });
      }
    },
    (error) => {
      console.error('Error leyendo estado del sistema:', error);
      // En caso de error, bloquear por seguridad
      callback({
        isActive: false,
        blockReason: 'error',
        status: 'error',
        maintenance: false,
        message: '',
        minVersion: 0,
        isStatusActive: false,
        isInMaintenance: false,
        isVersionBlocked: false,
        appVersion: APP_VERSION,
        blockMessage: 'Error de conexión con el sistema'
      });
    }
  );
  
  return unsubscribe;
};

/**
 * Obtener mensaje de bloqueo según la razón
 */
function getBlockMessage(reason) {
  switch (reason) {
    case 'maintenance':
      return 'Sistema en mantenimiento';
    case 'inactive':
      return 'Sistema temporalmente no disponible';
    case 'version':
      return 'Actualiza la aplicación para continuar';
    case 'no_config':
      return 'Configuración del sistema no encontrada';
    case 'error':
      return 'Error de conexión con el sistema';
    default:
      return null;
  }
}
